import type { AppointmentPlanningPreviewResponse } from "../../../../../../api/kehrblatt/planning"
import { resolveBatchApplyTargets } from "../../../_logic/batch-apply-targets"
import type {
  AppointmentPlanningExistingPlannedRow,
  AppointmentPlanningExistingSourceRow,
  AppointmentPlanningWorkOrderOverride,
} from "../../../_logic/appointments-contract"
import type { PlanningPreviewRow } from "../_shared/_logic/table-contract"
import type { AppointmentPlanningEmployeeOption } from "./settingsModel"
import { resolveReuseAppointmentPlanningStrategy } from "./payload"

export const employeeLabelByIdFromOptions = (
  employeeOptions: AppointmentPlanningEmployeeOption[],
): Record<string, string> =>
  Object.fromEntries(employeeOptions.map((option) => [String(option.value), option.label]))

export const rowOverrideEmployeeIds = (
  workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>,
): string[] =>
  Object.values(workOrderOverridesByRowId)
    .flatMap((override) => [override.appointment_employee_id ?? ""])
    .map(String)
    .filter(Boolean)

export const planningEmployeeIdsForSelection = ({
  selectedEmployees,
  workOrderOverridesByRowId,
}: {
  selectedEmployees: string[]
  workOrderOverridesByRowId: Record<string, AppointmentPlanningWorkOrderOverride>
}): string[] =>
  Array.from(
    new Set([
      ...selectedEmployees.map(String).filter(Boolean),
      ...rowOverrideEmployeeIds(workOrderOverridesByRowId),
    ]),
  )

export const filterExistingPlanningRows = (
  rows: AppointmentPlanningExistingSourceRow[],
  excludedRowIds: string[],
): AppointmentPlanningExistingSourceRow[] => rows.filter((row) => !excludedRowIds.includes(row.id))

export const isCompletedExistingPlanningRow = (
  row: AppointmentPlanningExistingPlannedRow,
): boolean => Boolean(row.event || row.preview)

export const completedExistingPlanningRowIds = (
  rows: AppointmentPlanningExistingPlannedRow[],
): Set<string> => new Set(rows.filter(isCompletedExistingPlanningRow).map((row) => row.id))

export const mergeExistingPlanningPreviewRows = ({
  previewRows,
  existingPlannedRows,
}: {
  previewRows: AppointmentPlanningPreviewResponse["rows"]
  existingPlannedRows: AppointmentPlanningExistingPlannedRow[]
}): Map<string, AppointmentPlanningPreviewResponse["rows"][number]> => {
  const previewByRowId = new Map(previewRows.map((row) => [row.client_row_id, row]))
  for (const row of existingPlannedRows) {
    if (!isCompletedExistingPlanningRow(row) || !row.preview) continue

    previewByRowId.set(row.id, row.preview)
    previewByRowId.set(row.source.workOrder.id, row.preview)
  }

  return previewByRowId
}

export const isAppointmentPlanningPreviewReady = ({
  sourceRowCount,
  startDate,
  selectedEmployees,
  existingRows = [],
  workOrderOverridesByRowId = {},
}: {
  sourceRowCount: number
  startDate: string
  selectedEmployees: string[]
  existingRows?: AppointmentPlanningExistingSourceRow[]
  workOrderOverridesByRowId?: Record<string, AppointmentPlanningWorkOrderOverride>
}): boolean => {
  if (!sourceRowCount || !startDate) return false

  if (selectedEmployees.length) return true

  return (
    existingRows.length > 0 &&
    existingRows.every((row) => {
      const strategy = resolveReuseAppointmentPlanningStrategy({
        row,
        override: workOrderOverridesByRowId[row.id],
      })

      return strategy === "KEEP_EXISTING" && Boolean(row.previousEvent?.id)
    })
  )
}

export const isAppointmentPlanningStepLocked = ({
  existingPlannedRows,
  existingRowIds = [],
}: {
  existingPlannedRows: AppointmentPlanningExistingPlannedRow[]
  existingRowIds?: string[]
}): boolean => {
  const expectedRowIds = Array.from(new Set(existingRowIds.map(String).filter(Boolean)))

  if (!expectedRowIds.length) return false

  const completedRowIds = completedExistingPlanningRowIds(existingPlannedRows)

  return expectedRowIds.every((rowId) => completedRowIds.has(rowId))
}

export const committedEventByWorkOrderId = ({
  existingPlannedRows,
}: {
  existingPlannedRows: AppointmentPlanningExistingPlannedRow[]
}): Map<string, ScheduleEvent> =>
  new Map(
    existingPlannedRows
      .filter(isCompletedExistingPlanningRow)
      .map((row) => row.event)
      .filter(Boolean)
      .map((event) => [event?.work_order_id ?? "", event as ScheduleEvent]),
  )

export const resolveAppointmentPlanningApplyTargets = ({
  previewRows,
  selectedIds,
}: {
  previewRows: PlanningPreviewRow[]
  selectedIds: string[]
}) => {
  const targetResolution = resolveBatchApplyTargets({
    filteredRows: previewRows,
    selectedIds,
    getId: (row) => row.id,
    isEligible: () => true,
  })

  return {
    ...targetResolution,
    targetCount: targetResolution.targetRows.length,
  }
}

export const hasAppointmentPlanningPreviewStatusMessages = ({
  previewRequirementsReady,
  previewError,
  previewResponse,
}: {
  previewRequirementsReady: boolean
  previewError: string | null
  previewResponse: AppointmentPlanningPreviewResponse | null
}): boolean =>
  previewRequirementsReady && Boolean(previewError || previewResponse?.optimization_warning)

export const isAppointmentPlanningRowEditingDisabled = ({
  readOnly,
  stepLocked,
  planning,
  deletingRowId,
}: {
  readOnly: boolean
  stepLocked: boolean
  planning: boolean
  deletingRowId: string | null
}): boolean => readOnly || stepLocked || planning || Boolean(deletingRowId)
